import { setDoc } from "../../auditedFirestore";
import { auditFetch as fetch } from "../../auditActivity";
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import { registerUser, notifyRegistration, ResendInviteButton } from "../../userInvitations";
import { doc, serverTimestamp } from "firebase/firestore";

const fmtTs = (value, empty = "—") => {
  if (!value) return empty;
  let d = null;
  if (value?.seconds) d = new Date(value.seconds * 1000);
  else if (value?.toDate) d = value.toDate();
  else d = new Date(value);
  return Number.isNaN(d?.getTime?.()) ? empty : d.toLocaleString("en-GB", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
};

const norm = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const compact = (v) => norm(v).replace(/\s+/g, "");
const words = (v) => norm(v).split(" ").filter(Boolean);
const normEmail = (v) => String(v || "").trim().toLowerCase();

function defaultLodgeName(contactName) {
  return String(contactName || "")
    .replace(/\bno\.?\s*\d{3,5}\b/i, "")
    .replace(/\(.*?\)/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function defaultLodgeNumber(contactName) {
  const m = String(contactName || "").match(/\b(\d{3,5})\b/);
  return m ? m[1] : "";
}

function scoreMatch(lodge, contact) {
  const lodgeName = lodge?.name || "";
  const contactName = contact?.name || "";
  const lodgeNorm = norm(lodgeName);
  const contactNorm = norm(contactName);
  const lodgeCompact = compact(lodgeName);
  const contactCompact = compact(contactName);
  const lodgeNumber = String(lodge?.number || "").trim();

  let score = 0;
  if (!contactName) return { score: 0, reason: "" };
  if (lodgeNumber && contactNorm.includes(lodgeNumber)) score += 45;
  if (lodgeNorm && contactNorm === lodgeNorm) score += 65;
  if (lodgeCompact && contactCompact === lodgeCompact) score += 35;
  if (lodgeNorm && contactNorm.includes(lodgeNorm)) score += 20;
  if (contactNorm && lodgeNorm.includes(contactNorm)) score += 10;

  const lodgeWords = words(lodgeName);
  const contactWords = new Set(words(contactName));
  const shared = lodgeWords.filter(w => contactWords.has(w));
  score += Math.min(25, shared.length * 8);

  return {
    score,
    reason: [
      lodgeNumber && contactNorm.includes(lodgeNumber) ? "number match" : "",
      shared.length ? `${shared.length} word match` : "",
      contactNorm === lodgeNorm ? "exact name" : "",
    ].filter(Boolean).join(", "),
  };
}

async function readJson(url, options) {
  const res = await fetch(url, options);
  const text = await res.text();
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = { raw: text }; }
  if (!res.ok) throw new Error(data?.error || data?.message || text || "Request failed");
  return data;
}

function card(theme) {
  return {
    background: theme.surface,
    borderRadius: 12,
    border: `1px solid ${theme.border}`,
    padding: "20px 22px",
  };
}

function btn(theme) {
  return {
    padding: "10px 16px",
    borderRadius: 8,
    border: "none",
    background: theme.primary,
    color: "#FFF",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'DM Sans',sans-serif",
  };
}

function btn2(theme) {
  return {
    padding: "10px 16px",
    borderRadius: 8,
    border: `1px solid ${theme.borderInput}`,
    background: "transparent",
    color: theme.primary,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'DM Sans',sans-serif",
  };
}

export default function XeroConsole({ lodges = [], bookings = [], users = [], xeroOn, xeroCfg, refreshXeroStatus, connectXero, notify, theme }) {
  const T = theme;
  const styles = { card: card(T), btn: btn(T), btn2: btn2(T) };

  const [contacts, setContacts] = useState([]);
  const [contactMeta, setContactMeta] = useState({ source: "cache", syncedAt: null, count: 0 });
  const [busy, setBusy] = useState(false);
  const [queries, setQueries] = useState({});
  const [selected, setSelected] = useState({});
  const [recon, setRecon] = useState(null);
  const [tab, setTab] = useState("workspace");
  const [contactSearch, setContactSearch] = useState("");
  const [drafts, setDrafts] = useState({});
  const [userDrafts, setUserDrafts] = useState({});

  async function loadContacts(refresh = false) {
    setBusy(true);
    try {
      const data = await readJson(`/xeroContacts${refresh ? "?refresh=1" : ""}`);
      setContacts(Array.isArray(data.contacts) ? data.contacts : []);
      setContactMeta({
        source: data.source || (refresh ? "xero" : "cache"),
        syncedAt: data.syncedAt || null,
        count: Array.isArray(data.contacts) ? data.contacts.length : 0,
      });
      notify(refresh ? "Xero contacts synced" : "Xero contacts loaded");
    } catch (e) {
      console.error(e);
      notify(`Failed to load Xero contacts: ${e.message}`, "error");
    } finally {
      setBusy(false);
    }
  }

  async function loadReconciliation() {
    try {
      const data = await readJson("/xeroReconciliation");
      setRecon(data);
    } catch (e) {
      console.error(e);
      notify(`Failed to load reconciliation: ${e.message}`, "error");
    }
  }

  useEffect(() => {
    loadContacts(false);
    loadReconciliation();
  }, []);

  useEffect(() => {
    setSelected(prev => {
      const next = { ...prev };
      lodges.forEach(l => {
        if (l?.xeroContactId && !next[l.id || l.number]) next[l.id || l.number] = l.xeroContactId;
      });
      return next;
    });
  }, [lodges]);

  useEffect(() => {
    setDrafts(prev => {
      const next = { ...prev };
      contacts.forEach(c => {
        if (!next[c.contactId]) {
          next[c.contactId] = {
            name: defaultLodgeName(c.name),
            number: defaultLodgeNumber(c.name),
          };
        }
      });
      return next;
    });
  }, [contacts]);

  const contactIdsInUse = useMemo(() => new Set(lodges.filter(l => !!l.xeroContactId).map(l => String(l.xeroContactId))), [lodges]);

  const unmatchedContacts = useMemo(() => {
    const q = norm(contactSearch);
    return contacts.filter(c => {
      if (contactIdsInUse.has(String(c.contactId))) return false;
      if (!q) return true;
      return norm(c.name).includes(q) || String(c.contactId || "").toLowerCase().includes(q) || String(c.email || "").toLowerCase().includes(q);
    });
  }, [contacts, contactIdsInUse, contactSearch]);

  const lodgeSuggestions = useMemo(() => {
    const out = {};
    lodges.forEach(lodge => {
      const ranked = contacts
        .map(c => ({ contact: c, ...scoreMatch(lodge, c) }))
        .filter(x => x.score > 0)
        .sort((a, b) => b.score - a.score);
      out[lodge.id || lodge.number] = ranked[0] || null;
    });
    return out;
  }, [lodges, contacts]);

  const localSummary = useMemo(() => {
    const sentToKitchen = bookings.filter(b => b.status === "sent_to_kitchen");
    const invoiceErrors = bookings.filter(b => b.xeroInvoiceStatus === "error" || b.xeroError);
    const emailErrors = bookings.filter(b => b.xeroEmailStatus === "error" || b.xeroEmailError);
    const overdueScheduled = bookings.filter(b => !b.xeroEmailSent && b.xeroEmailScheduledAt && new Date((b.xeroEmailScheduledAt.seconds ? b.xeroEmailScheduledAt.seconds * 1000 : b.xeroEmailScheduledAt)) < new Date());
    return {
      sentToKitchen: sentToKitchen.length,
      invoiceGenerated: sentToKitchen.filter(b => b.xeroInvoiceId || b.xeroInvoiceStatus === "generated").length,
      invoiceErrors: invoiceErrors.length,
      emailErrors: emailErrors.length,
      overdueScheduled: overdueScheduled.length,
      mappedLodges: lodges.filter(l => !!l.xeroContactId).length,
      unmappedLodges: lodges.filter(l => !l.xeroContactId).length,
    };
  }, [bookings, lodges]);

  const recentIssues = useMemo(() => {
    return bookings
      .filter(b => b.xeroInvoiceStatus === "error" || b.xeroEmailStatus === "error" || b.xeroError || b.xeroEmailError)
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
      .slice(0, 12);
  }, [bookings]);

  // ─── Treasurer login grouping (by Xero contact email) ─────────────────────
  const contactEmailById = useMemo(() => {
    const m = {};
    contacts.forEach(c => { if (c && c.contactId) m[String(c.contactId)] = c.email || ""; });
    return m;
  }, [contacts]);

  const contactNameById = useMemo(() => {
    const m = {};
    contacts.forEach(c => { if (c && c.contactId) m[String(c.contactId)] = c.name || ""; });
    return m;
  }, [contacts]);

  const resolveLodgeEmail = (l) =>
    normEmail(l.xeroContactEmail || contactEmailById[String(l.xeroContactId)] || "");

  const usersByEmail = useMemo(() => {
    const m = {};
    users.forEach(u => { const e = normEmail(u.email); if (e) m[e] = u; });
    return m;
  }, [users]);

  const treasurerGroups = useMemo(() => {
    const groups = {};
    lodges.forEach(l => {
      const email = resolveLodgeEmail(l);
      if (!email) return;
      if (!groups[email]) groups[email] = { email, lodges: [], contactName: "" };
      groups[email].lodges.push({ number: String(l.number || ""), name: l.name || "" });
      if (!groups[email].contactName) {
        groups[email].contactName = l.xeroContactName || contactNameById[String(l.xeroContactId)] || "";
      }
    });
    return Object.values(groups).sort((a, b) => a.email.localeCompare(b.email));
  }, [lodges, contactEmailById, contactNameById]);

  const noEmailLodges = useMemo(
    () => lodges.filter(l => !resolveLodgeEmail(l)),
    [lodges, contactEmailById]
  );

  function mergeLodgeAccess(existing, additions) {
    const seen = new Set();
    const out = [];
    const push = (l) => {
      const num = String(l?.number || "").trim();
      if (num && !seen.has(num)) { seen.add(num); out.push({ number: num, name: String(l?.name || "").trim() }); }
    };
    (existing || []).forEach(push);
    (additions || []).forEach(push);
    return out;
  }

  async function saveMapping(lodge, contactId) {
    if (!contactId) {
      notify("Choose a Xero contact first", "error");
      return;
    }
    setBusy(true);
    try {
      await readJson("/xeroMapLodge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lodgeId: String(lodge.id || lodge.number), contactId }),
      });
      notify(`Mapped ${lodge.name}`);
      await loadContacts(false);
      await loadReconciliation();
    } catch (e) {
      console.error(e);
      notify(`Failed to map lodge: ${e.message}`, "error");
    } finally {
      setBusy(false);
    }
  }

  async function clearMapping(lodge) {
    setBusy(true);
    try {
      await readJson("/xeroMapLodge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lodgeId: String(lodge.id || lodge.number), clear: true }),
      });
      notify(`Cleared mapping for ${lodge.name}`);
      await loadContacts(false);
      await loadReconciliation();
    } catch (e) {
      console.error(e);
      notify(`Failed to clear mapping: ${e.message}`, "error");
    } finally {
      setBusy(false);
    }
  }

  async function applyAutoMatch() {
    const candidates = lodges
      .map(l => ({ lodge: l, suggestion: lodgeSuggestions[l.id || l.number] }))
      .filter(x => !x.lodge.xeroContactId && x.suggestion && x.suggestion.score >= 60);
    if (!candidates.length) {
      notify("No confident matches found");
      return;
    }
    setBusy(true);
    let success = 0;
    try {
      for (const row of candidates) {
        await readJson("/xeroMapLodge", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lodgeId: String(row.lodge.id || row.lodge.number), contactId: row.suggestion.contact.contactId }),
        });
        success += 1;
      }
      notify(`Applied ${success} automatic mapping${success === 1 ? "" : "s"}`);
      await loadContacts(false);
      await loadReconciliation();
    } catch (e) {
      console.error(e);
      notify(`Automatic matching stopped: ${e.message}`, "error");
    } finally {
      setBusy(false);
    }
  }

  async function retryInvoice(bookingId) {
    setBusy(true);
    try {
      const data = await readJson("/xeroRetryInvoice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bookingId }),
      });
      notify(data.message || "Invoice retry queued");
      await loadReconciliation();
    } catch (e) {
      console.error(e);
      notify(`Invoice retry failed: ${e.message}`, "error");
    } finally {
      setBusy(false);
    }
  }

  async function retryEmail(bookingId) {
    setBusy(true);
    try {
      const data = await readJson("/xeroRetryEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bookingId }),
      });
      notify(data.message || "Email retry sent");
      await loadReconciliation();
    } catch (e) {
      console.error(e);
      notify(`Email retry failed: ${e.message}`, "error");
    } finally {
      setBusy(false);
    }
  }

  async function createLodgeFromContact(contact) {
    const draft = drafts[contact.contactId] || { name: defaultLodgeName(contact.name), number: defaultLodgeNumber(contact.name) };
    const number = String(draft.number || "").trim();
    const name = String(draft.name || "").trim();

    if (!number || !name) {
      notify("Please enter a lodge number and name first", "error");
      return;
    }
    if (lodges.some(l => String(l.number) === number)) {
      notify(`A lodge with number ${number} already exists`, "error");
      return;
    }

    setBusy(true);
    try {
      await setDoc(doc(db, "lodges", number), {
        number,
        name,
        logo: null,
        xeroContactId: contact.contactId,
        xeroContactName: contact.name || "",
        xeroContactEmail: contact.email || "",
        xeroContactStatus: contact.status || "",
        xeroMappedAt: serverTimestamp(),
        xeroMappedBy: "xero-console-create",
        createdFromXero: true,
      }, { merge: true });
      notify(`Created lodge ${name}`);
      await loadContacts(false);
      await loadReconciliation();
    } catch (e) {
      console.error(e);
      notify(`Failed to create lodge: ${e.message}`, "error");
    } finally {
      setBusy(false);
    }
  }

  async function createTreasurer(group) {
    const email = normEmail(group.email);
    if (!email) { notify("This lodge group has no email", "error"); return; }
    if (usersByEmail[email]) { notify("A login already exists for this email", "error"); return; }
    const draft = userDrafts[email] || {};
    const name = String(draft.name != null ? draft.name : group.contactName || "").trim();
    const access = mergeLodgeAccess([], group.lodges);
    const primary = access[0] || { number: "", name: "" };
    setBusy(true);
    try {
      const result = await registerUser({
        name,
        email,
        phone: "",
        role: "treasurer",
        lodgeNumber: primary.number,
        lodgeName: primary.name,
        lodgeAccess: access,
        activeLodgeNumber: primary.number,
        activeLodgeName: primary.name,
        active: true,
        createdFromXero: true,
        createdAt: serverTimestamp(),
      });
      notifyRegistration(result, notify);
    } catch (e) {
      console.error(e);
      notify(`Failed to create login: ${e.message}`, "error");
    } finally {
      setBusy(false);
    }
  }

  async function syncTreasurer(group, existingUser) {
    if (!existingUser) { notify("No existing login to sync", "error"); return; }
    const access = mergeLodgeAccess(existingUser.lodgeAccess, group.lodges);
    const primary = access[0] || { number: existingUser.lodgeNumber || "", name: existingUser.lodgeName || "" };
    setBusy(true);
    try {
      await setDoc(doc(db, "users", String(existingUser.id)), {
        lodgeAccess: access,
        lodgeNumber: existingUser.lodgeNumber || primary.number,
        lodgeName: existingUser.lodgeName || primary.name,
        activeLodgeNumber: existingUser.activeLodgeNumber || primary.number,
        activeLodgeName: existingUser.activeLodgeName || primary.name,
        role: existingUser.role || "treasurer",
        active: existingUser.active !== false,
      }, { merge: true });
      notify(`Synced lodges for ${normEmail(group.email)}`);
    } catch (e) {
      console.error(e);
      notify(`Failed to sync lodges: ${e.message}`, "error");
    } finally {
      setBusy(false);
    }
  }

  const tabs = [
    { id: "workspace", label: "Workspace" },
    { id: "treasurers", label: "Treasurers" },
    { id: "queue", label: "Retry Queue" },
    { id: "recon", label: "Reconciliation" },
  ];

  return (
    <div style={{ animation: "fadeUp .4s ease" }}>
      <div style={{ display:"flex", justifyContent:"space-between", gap:12, flexWrap:"wrap", marginBottom: 22 }}>
        <div>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:26, color:T.text, margin:0 }}>Xero Console</h1>
          <p style={{ color:T.textMuted, margin:"4px 0 0", fontSize:13 }}>
            Mapping, lodge creation, sync, retry and reconciliation for the Xero integration.
          </p>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <button onClick={() => refreshXeroStatus()} style={styles.btn2}>Refresh Status</button>
          <button onClick={() => loadContacts(true)} style={styles.btn2} disabled={busy}>Sync Contacts</button>
          <button onClick={() => applyAutoMatch()} style={styles.btn2} disabled={busy || !contacts.length}>Auto-match Lodges</button>
          <button onClick={() => connectXero()} style={styles.btn}>{xeroOn ? "Reconnect Xero" : "Connect Xero"}</button>
        </div>
      </div>

      <div style={{ ...styles.card, marginBottom:18, background:xeroOn ? T.successBg : T.warningBg, border:`1px solid ${xeroOn ? T.successBorder : T.warningBorder}` }}>
        <div style={{ display:"flex", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
          <div>
            <div style={{ fontWeight:700, color:xeroOn ? T.success : T.warning, marginBottom:4 }}>{xeroOn ? "Connected" : "Not connected"}</div>
            <div style={{ fontSize:13, color:T.textMuted }}>Tenant: {xeroCfg?.tenantName || "Unknown"}</div>
            {xeroCfg?.tenantId && <div style={{ fontSize:12, color:T.textMuted, marginTop:4 }}>Tenant ID: {xeroCfg.tenantId}</div>}
          </div>
          <div style={{ minWidth: 230 }}>
            <div style={{ fontSize:12, color:T.textMuted }}>Contacts cached: <strong style={{ color:T.text }}>{contactMeta.count || contacts.length}</strong></div>
            <div style={{ fontSize:12, color:T.textMuted, marginTop:4 }}>Last sync: {fmtTs(contactMeta.syncedAt)}</div>
            <div style={{ fontSize:12, color:T.textMuted, marginTop:4 }}>Source: {contactMeta.source || "cache"}</div>
          </div>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:14, marginBottom:18 }}>
        {[
          ["Mapped lodges", (recon?.summary?.mappedLodges ?? localSummary.mappedLodges)],
          ["Unmapped lodges", (recon?.summary?.unmappedLodges ?? localSummary.unmappedLodges)],
          ["Synced contacts", contactMeta.count || contacts.length],
          ["Invoices generated", (recon?.summary?.invoiceGenerated ?? localSummary.invoiceGenerated)],
          ["Invoice errors", (recon?.summary?.invoiceErrors ?? localSummary.invoiceErrors)],
          ["Email errors", (recon?.summary?.emailErrors ?? localSummary.emailErrors)],
        ].map(([label, value]) => (
          <div key={label} style={styles.card}>
            <div style={{ fontSize:11, fontWeight:700, color:T.textMuted, textTransform:"uppercase", marginBottom:6 }}>{label}</div>
            <div style={{ fontSize:28, fontWeight:700, color:T.text, fontFamily:"'Playfair Display',serif" }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ ...(tab === t.id ? styles.btn : styles.btn2), padding:"8px 14px" }}>{t.label}</button>
        ))}
      </div>

      {tab === "workspace" && (
        <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1.2fr) minmax(360px,0.9fr)", gap:18, alignItems:"start" }}>
          <div style={{ ...styles.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"16px 18px", borderBottom:`1px solid ${T.border}`, background:T.surfaceAlt, display:"flex", justifyContent:"space-between", gap:12, flexWrap:"wrap", alignItems:"center" }}>
              <div style={{ fontSize:12, color:T.textMuted }}>
                <strong style={{ color:T.text }}>Existing lodges</strong> are mapped to Xero by <strong style={{ color:T.text }}>xeroContactId</strong>.
              </div>
              <button onClick={() => applyAutoMatch()} style={styles.btn2} disabled={busy || !contacts.length}>Auto-match Lodges</button>
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr style={{ borderBottom:`2px solid ${T.border}`, background:T.surfaceAlt }}>
                    {["Lodge", "Current", "Search", "Contact", "Suggestion", "Actions"].map(h => (
                      <th key={h} style={{ textAlign:"left", padding:"10px 12px", fontSize:10, fontWeight:700, textTransform:"uppercase", color:T.textMuted, whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lodges.map(lodge => {
                    const key = lodge.id || lodge.number;
                    const q = queries[key] || "";
                    const filtered = contacts
                      .filter(c => !q || norm(c.name).includes(norm(q)) || String(c.contactId || "").toLowerCase().includes(q.toLowerCase()))
                      .slice(0, 30);
                    const suggestion = lodgeSuggestions[key];
                    return (
                      <tr key={key} style={{ borderBottom:`1px solid ${T.surfaceAlt}`, verticalAlign:"top" }}>
                        <td style={{ padding:"12px" }}><div style={{ fontWeight:700 }}>{lodge.name}</div><div style={{ fontSize:11, color:T.textMuted }}>#{lodge.number}</div></td>
                        <td style={{ padding:"12px", minWidth:200 }}>
                          <div style={{ fontWeight:600, color:lodge.xeroContactId ? T.success : T.textMuted }}>{lodge.xeroContactName || lodge.xeroContactId || "Not mapped"}</div>
                          {lodge.xeroMappedAt && <div style={{ fontSize:11, color:T.textMuted, marginTop:4 }}>Mapped: {fmtTs(lodge.xeroMappedAt)}</div>}
                        </td>
                        <td style={{ padding:"12px", minWidth:180 }}><input value={q} onChange={e => setQueries(prev => ({ ...prev, [key]: e.target.value }))} placeholder="Search contact" style={{ width:"100%", padding:"8px 10px", borderRadius:8, border:`1px solid ${T.borderInput}`, background:T.surface, color:T.text, fontFamily:"inherit", fontSize:13 }} /></td>
                        <td style={{ padding:"12px", minWidth:260 }}>
                          <select value={selected[key] || lodge.xeroContactId || ""} onChange={e => setSelected(prev => ({ ...prev, [key]: e.target.value }))} style={{ width:"100%", padding:"9px 10px", borderRadius:8, border:`1px solid ${T.borderInput}`, background:T.surface, color:T.text, fontFamily:"inherit", fontSize:13 }}>
                            <option value="">Choose contact…</option>
                            {filtered.map(c => <option key={c.contactId} value={c.contactId}>{c.name}{c.email ? ` — ${c.email}` : ""}</option>)}
                          </select>
                        </td>
                        <td style={{ padding:"12px", minWidth:160 }}>
                          {suggestion ? <div><div style={{ fontWeight:600 }}>{suggestion.contact.name}</div><div style={{ fontSize:11, color:suggestion.score >= 60 ? T.success : T.warning, marginTop:4 }}>Score {suggestion.score}</div><div style={{ fontSize:11, color:T.textMuted, marginTop:2 }}>{suggestion.reason || "name similarity"}</div></div> : <span style={{ color:T.textMuted }}>No suggestion</span>}
                        </td>
                        <td style={{ padding:"12px", whiteSpace:"nowrap" }}>
                          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                            <button onClick={() => saveMapping(lodge, selected[key] || lodge.xeroContactId)} style={{ ...styles.btn, padding:"8px 12px" }} disabled={busy}>Save</button>
                            {suggestion && <button onClick={() => saveMapping(lodge, suggestion.contact.contactId)} style={{ ...styles.btn2, padding:"8px 12px" }} disabled={busy}>Use suggestion</button>}
                            {lodge.xeroContactId && <button onClick={() => clearMapping(lodge)} style={{ ...styles.btn2, padding:"8px 12px" }} disabled={busy}>Clear</button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!lodges.length && <tr><td colSpan={6} style={{ padding:28, textAlign:"center", color:T.textMuted }}>No lodges found.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ ...styles.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"16px 18px", borderBottom:`1px solid ${T.border}`, background:T.surfaceAlt, display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ fontSize:12, color:T.textMuted }}>
                <strong style={{ color:T.text }}>Unmatched Xero contacts</strong> can be turned straight into lodges here.
              </div>
              <input value={contactSearch} onChange={e => setContactSearch(e.target.value)} placeholder="Search unmatched contacts" style={{ width:"100%", padding:"8px 10px", borderRadius:8, border:`1px solid ${T.borderInput}`, background:T.surface, color:T.text, fontFamily:"inherit", fontSize:13 }} />
            </div>
            <div style={{ maxHeight:720, overflow:"auto" }}>
              {unmatchedContacts.length ? unmatchedContacts.map(contact => {
                const draft = drafts[contact.contactId] || { name: defaultLodgeName(contact.name), number: defaultLodgeNumber(contact.name) };
                return (
                  <div key={contact.contactId} style={{ padding:"14px 16px", borderBottom:`1px solid ${T.surfaceAlt}` }}>
                    <div style={{ fontWeight:700, color:T.text }}>{contact.name}</div>
                    <div style={{ fontSize:11, color:T.textMuted, marginTop:4, wordBreak:"break-all" }}>{contact.contactId}</div>
                    <div style={{ fontSize:12, color:T.textMuted, marginTop:4 }}>{contact.email || "No email on contact"}</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 120px", gap:10, marginTop:12 }}>
                      <input value={draft.name || ""} onChange={e => setDrafts(prev => ({ ...prev, [contact.contactId]: { ...draft, name: e.target.value } }))} placeholder="Lodge name" style={{ width:"100%", padding:"8px 10px", borderRadius:8, border:`1px solid ${T.borderInput}`, background:T.surface, color:T.text, fontFamily:"inherit", fontSize:13 }} />
                      <input value={draft.number || ""} onChange={e => setDrafts(prev => ({ ...prev, [contact.contactId]: { ...draft, number: e.target.value.replace(/[^0-9]/g, "") } }))} placeholder="Number" style={{ width:"100%", padding:"8px 10px", borderRadius:8, border:`1px solid ${T.borderInput}`, background:T.surface, color:T.text, fontFamily:"inherit", fontSize:13 }} />
                    </div>
                    <div style={{ display:"flex", gap:8, marginTop:12, flexWrap:"wrap" }}>
                      <button onClick={() => createLodgeFromContact(contact)} style={{ ...styles.btn, padding:"8px 12px" }} disabled={busy}>Create Lodge</button>
                    </div>
                  </div>
                );
              }) : <div style={{ padding:28, textAlign:"center", color:T.textMuted }}>No unmatched Xero contacts found.</div>}
            </div>
          </div>
        </div>
      )}

      {tab === "treasurers" && (
        <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
          <div style={{ ...styles.card, padding:"14px 18px" }}>
            <div style={{ fontSize:12, color:T.textMuted }}>
              <strong style={{ color:T.text }}>Treasurer logins</strong> are grouped by the email on each lodge's Xero contact. One login covers every lodge sharing that email. Create a login here and that treasurer can sign in with this email; their account links automatically on first sign-in.
            </div>
          </div>

          <div style={{ ...styles.card, padding:0, overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr style={{ borderBottom:`2px solid ${T.border}`, background:T.surfaceAlt }}>
                    {["Email", "Lodges", "Login", "Treasurer name", "Action"].map(h => (
                      <th key={h} style={{ textAlign:"left", padding:"10px 12px", fontSize:10, fontWeight:700, textTransform:"uppercase", color:T.textMuted, whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {treasurerGroups.map(group => {
                    const existing = usersByEmail[group.email];
                    const existingNums = new Set(
                      existing?.lodgeAccess?.length
                        ? existing.lodgeAccess.map(l => String(l.number))
                        : existing?.lodgeNumber ? [String(existing.lodgeNumber)] : []
                    );
                    const missing = group.lodges.filter(l => !existingNums.has(String(l.number)));
                    const draft = userDrafts[group.email] || {};
                    const nameVal = draft.name != null ? draft.name : (existing?.name || group.contactName || "");
                    return (
                      <tr key={group.email} style={{ borderBottom:`1px solid ${T.surfaceAlt}`, verticalAlign:"top" }}>
                        <td style={{ padding:"12px", wordBreak:"break-all" }}>{group.email}</td>
                        <td style={{ padding:"12px", minWidth:200 }}>
                          <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                            {group.lodges.map(l => (
                              <span key={l.number} style={{ fontSize:11, padding:"2px 8px", borderRadius:20, background:T.surfaceAlt, border:`1px solid ${T.border}`, color:T.textSub }}>{l.name || `#${l.number}`}{l.number ? ` · #${l.number}` : ""}</span>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding:"12px", minWidth:130 }}>
                          {!existing && <span style={{ fontSize:11, fontWeight:700, color:T.warning, background:T.warningBg, padding:"3px 8px", borderRadius:10 }}>Not created</span>}
                          {existing && missing.length === 0 && <span style={{ fontSize:11, fontWeight:700, color:T.success, background:T.successBg, padding:"3px 8px", borderRadius:10 }}>Active</span>}
                          {existing && missing.length > 0 && <span style={{ fontSize:11, fontWeight:700, color:T.warning, background:T.warningBg, padding:"3px 8px", borderRadius:10 }}>Needs {missing.length} lodge{missing.length === 1 ? "" : "s"}</span>}
                          {existing && existing.active === false && <div style={{ fontSize:11, color:T.danger, marginTop:4 }}>Disabled</div>}
                        </td>
                        <td style={{ padding:"12px", minWidth:170 }}>
                          <input
                            value={nameVal}
                            onChange={e => setUserDrafts(prev => ({ ...prev, [group.email]: { ...prev[group.email], name: e.target.value } }))}
                            placeholder="Treasurer name"
                            style={{ width:"100%", padding:"8px 10px", borderRadius:8, border:`1px solid ${T.borderInput}`, background:T.surface, color:T.text, fontFamily:"inherit", fontSize:13 }}
                          />
                        </td>
                        <td style={{ padding:"12px", whiteSpace:"nowrap" }}>
                          {existing && <ResendInviteButton user={existing} notify={notify} style={styles.btn2} />}
                          {!existing && <button onClick={() => createTreasurer(group)} style={{ ...styles.btn, padding:"8px 12px" }} disabled={busy}>Create login</button>}
                          {existing && missing.length > 0 && <button onClick={() => syncTreasurer(group, existing)} style={{ ...styles.btn, padding:"8px 12px" }} disabled={busy}>Sync lodges</button>}
                          {existing && missing.length === 0 && <span style={{ fontSize:12, color:T.textMuted }}>Up to date</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {!treasurerGroups.length && <tr><td colSpan={5} style={{ padding:28, textAlign:"center", color:T.textMuted }}>No lodge contact emails found yet. Map or create lodges from Xero contacts first, then Sync Contacts.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {noEmailLodges.length > 0 && (
            <div style={styles.card}>
              <div style={{ fontSize:12, fontWeight:700, color:T.warning, marginBottom:8 }}>Lodges with no contact email ({noEmailLodges.length})</div>
              <div style={{ fontSize:12, color:T.textMuted, marginBottom:10 }}>These lodges have no email on their Xero contact, so no treasurer login can be created for them here. Add an email to the Xero contact (or map the lodge to a contact that has one), then use Sync Contacts.</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {noEmailLodges.map(l => (
                  <span key={l.number} style={{ fontSize:11, padding:"2px 8px", borderRadius:20, background:T.surfaceAlt, border:`1px solid ${T.border}`, color:T.textSub }}>{l.name} · #{l.number}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "queue" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:18 }}>
          <div style={styles.card}>
            <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:18, margin:"0 0 14px" }}>Invoice Retry Queue</h3>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {bookings.filter(b => b.xeroInvoiceStatus === "error" || b.xeroError).slice(0, 20).map(b => (
                <div key={b.id} style={{ border:`1px solid ${T.border}`, borderRadius:10, padding:"12px 14px" }}>
                  <div style={{ fontWeight:700 }}>{b.lodgeName} — {b.date}</div>
                  <div style={{ fontSize:12, color:T.textMuted, marginTop:4 }}>Booking ID: {b.id}</div>
                  <div style={{ fontSize:12, color:T.danger, marginTop:6 }}>{b.xeroError || "Invoice error"}</div>
                  <div style={{ marginTop:10 }}><button onClick={() => retryInvoice(b.id)} style={{ ...styles.btn, padding:"8px 12px" }} disabled={busy}>Retry invoice</button></div>
                </div>
              ))}
              {!bookings.some(b => b.xeroInvoiceStatus === "error" || b.xeroError) && <div style={{ color:T.textMuted }}>No invoice failures currently.</div>}
            </div>
          </div>

          <div style={styles.card}>
            <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:18, margin:"0 0 14px" }}>Failed / Overdue Email Recovery</h3>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {bookings.filter(b => b.xeroInvoiceId && (b.xeroEmailStatus === "error" || b.xeroEmailError || (!b.xeroEmailSent && b.xeroEmailScheduledAt))).slice(0, 20).map(b => (
                <div key={b.id} style={{ border:`1px solid ${T.border}`, borderRadius:10, padding:"12px 14px" }}>
                  <div style={{ fontWeight:700 }}>{b.lodgeName} — {b.date}</div>
                  <div style={{ fontSize:12, color:T.textMuted, marginTop:4 }}>Invoice: {b.xeroInvoiceNumber || b.xeroInvoiceId}</div>
                  <div style={{ fontSize:12, color:(b.xeroEmailStatus === "error" || b.xeroEmailError) ? T.danger : T.warning, marginTop:6 }}>{b.xeroEmailError || `Scheduled: ${fmtTs(b.xeroEmailScheduledAt)}`}</div>
                  <div style={{ marginTop:10 }}><button onClick={() => retryEmail(b.id)} style={{ ...styles.btn, padding:"8px 12px" }} disabled={busy}>Retry email</button></div>
                </div>
              ))}
              {!bookings.some(b => b.xeroInvoiceId && (b.xeroEmailStatus === "error" || b.xeroEmailError || (!b.xeroEmailSent && b.xeroEmailScheduledAt))) && <div style={{ color:T.textMuted }}>No email retries needed.</div>}
            </div>
          </div>
        </div>
      )}

      {tab === "recon" && (
        <div style={{ display:"grid", gridTemplateColumns:"1.3fr 1fr", gap:18 }}>
          <div style={styles.card}>
            <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:18, margin:"0 0 14px" }}>Recent Xero Issues</h3>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr style={{ borderBottom:`2px solid ${T.border}` }}>
                    {["Date", "Lodge", "Invoice", "Email", "Actions"].map(h => <th key={h} style={{ textAlign:"left", padding:"8px 10px", fontSize:10, fontWeight:700, textTransform:"uppercase", color:T.textMuted }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {recentIssues.map(b => (
                    <tr key={b.id} style={{ borderBottom:`1px solid ${T.surfaceAlt}` }}>
                      <td style={{ padding:"10px" }}>{b.date}</td>
                      <td style={{ padding:"10px" }}>{b.lodgeName}</td>
                      <td style={{ padding:"10px", color:b.xeroError ? T.danger : T.textMuted }}>{b.xeroError || b.xeroInvoiceStatus || "—"}</td>
                      <td style={{ padding:"10px", color:b.xeroEmailError ? T.danger : T.textMuted }}>{b.xeroEmailError || b.xeroEmailStatus || "—"}</td>
                      <td style={{ padding:"10px", whiteSpace:"nowrap" }}>
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                          {(b.xeroError || b.xeroInvoiceStatus === "error") && <button onClick={() => retryInvoice(b.id)} style={{ ...styles.btn2, padding:"6px 10px" }}>Retry invoice</button>}
                          {b.xeroInvoiceId && (b.xeroEmailError || b.xeroEmailStatus === "error" || !b.xeroEmailSent) && <button onClick={() => retryEmail(b.id)} style={{ ...styles.btn2, padding:"6px 10px" }}>Retry email</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!recentIssues.length && <tr><td colSpan={5} style={{ padding:28, textAlign:"center", color:T.textMuted }}>No recent Xero issues.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div style={styles.card}>
            <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:18, margin:"0 0 14px" }}>Reconciliation Snapshot</h3>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {[
                ["Sent to kitchen", recon?.summary?.sentToKitchen ?? localSummary.sentToKitchen],
                ["Invoices generated", recon?.summary?.invoiceGenerated ?? localSummary.invoiceGenerated],
                ["Invoices pending", recon?.summary?.invoicePending ?? Math.max(0, localSummary.sentToKitchen - localSummary.invoiceGenerated - localSummary.invoiceErrors)],
                ["Emails sent", recon?.summary?.emailSent ?? 0],
                ["Emails pending", recon?.summary?.emailPending ?? 0],
                ["Emails overdue", recon?.summary?.overdueScheduled ?? localSummary.overdueScheduled],
              ].map(([k, v]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"10px 12px", border:`1px solid ${T.border}`, borderRadius:8 }}>
                  <span style={{ color:T.textMuted }}>{k}</span>
                  <strong style={{ color:T.text }}>{v}</strong>
                </div>
              ))}
            </div>
            {recon?.generatedAt && <div style={{ fontSize:12, color:T.textMuted, marginTop:14 }}>Generated: {fmtTs(recon.generatedAt)}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
